import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "@redux/hooks";
import type { RootState } from "@redux/store/store";
import GenericCard from "@ui-components/Card/GenericCard";
import {
  GenericTable,
  type GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import { GenericButton } from "@ui-components/Button/GenericButton";
import { useAlerts } from "@hooks/useAlerts";
import { ReactComponent as IconEdit } from "@assets/Icons/IconEdit_KE.svg";
import { ReactComponent as IconTrash } from "@assets/Icons/IconDelete_KE.svg";
import {
  buildPredicate,
  type ParsedFilter,
} from "kontrolEdge/helpers/advancedFilters";
import { normalizeFilters } from "kontrolEdge/helpers/normalizeFilters";
import EmptyItems from "@shared/components/EmptyItems";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import {
  ListPageSkeleton,
  RefreshingIndicator,
} from "@shared/components/ListLoading";

import {
  matchesCategorySelection,
  matchesProductSelection,
  matchesTagSelection,
  tagsColumns as baseColumns,
  type TagRow,
} from "./TagsData";
import { makeTagsFields } from "./tagsFieldSpec";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { useEffectiveTenantId } from "@hooks/useEffectiveTenantId";
import { useTagRows } from "./hooks";
import {
  deleteTag,
  getTagResourceCount,
  TagDeleteConflictError,
} from "./requests/tagsStore";
import { productToMount } from "./requests/productAdapters";
import type { Tag } from "./types";

/**
 * Single stat block rendered inside the unified KPI card. Visual language
 * mirrors the Backbone detail header (`BackboneDetails` Bandwidth /
 * Connectors cards) — secondary-tinted label on top, prominent value
 * below in brand color (or warning / success accent) and an optional
 * secondary hint. No own `GenericCard` wrapper: the parent owns the
 * shell so all four KPIs sit inside the same surface, matching the
 * reference's "indicators inside one card" pattern.
 */
const Stat: React.FC<{
  label: string;
  value: number | string;
  accent?: "default" | "warning" | "success";
  hint?: string;
}> = ({ label, value, accent = "default", hint }) => {
  const valueColor =
    accent === "warning"
      ? "text-semantic-text-warning"
      : accent === "success"
      ? "text-semantic-text-success"
      : "text-semantic-text-brand";
  return (
    <div className="flex flex-col min-w-0">
      <span className="text-xs uppercase tracking-wide text-semantic-text-secondary">
        {label}
      </span>
      <span className={`text-2xl font-semibold mt-1 tabular-nums ${valueColor}`}>
        {value}
      </span>
      {hint && (
        <span className="text-xs text-semantic-text-secondary mt-1 truncate">
          {hint}
        </span>
      )}
    </div>
  );
};

const TagsManagerPage: React.FC = () => {
  const navigate = useNavigate();
  const api = useAxiosAuth0();
  const selectedProduct = useAppSelector(
    (state: RootState) => state.product.selectedProduct
  );
  const mount = productToMount(selectedProduct);
  // Tenant lens — `undefined` in partner view (JWT-resolved on the
  // backend), the child tenant's id otherwise. See the hook for the
  // exact semantics.
  const tenantId = useEffectiveTenantId();
  // Partner-view detection mirrors the convention used by Ports / FWaaS
  // / DCI etc.: when the selected tenant equals the partner's own id, we
  // are looking at "all customers" and inject an extra Customer column
  // sourced from the API's embedded `tenant.name`.
  const isViewingAllCustomers = useAppSelector(
    (state: RootState) =>
      state.selection.tenantId === state.selection.partnerId
  );
  const { rows, loading, error, hasFetched, refresh } = useTagRows();

  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);
  const { confirm, alert, Alert } = useAlerts();

  const showInitialSkeleton = !hasFetched || (loading && rows.length === 0);
  // Rows already on screen while a newer answer lands: the thin bar, not a
  // spinner — the table stays readable and the rail stays clickable.
  const isRefreshingList = loading && rows.length > 0;

  const fields = useMemo(
    () => makeTagsFields(isViewingAllCustomers),
    [isViewingAllCustomers]
  );
  const suggestedKeys = useMemo(() => fields.map((f) => f.label), [fields]);

  const {
    tagFilterSelection,
    categoryFilterSelection,
    productFilterSelection,
  } = useAppSelector((state: RootState) => state.tagsManagerFilters);

  const normalized = useMemo(
    () =>
      normalizeFilters<TagRow>(activeFilters, fields, {
        defaultStringToContains: true,
        quotedStringsExact: true,
      }),
    [activeFilters, fields]
  );

  const searchPredicate = useMemo(
    () => buildPredicate<TagRow>(normalized, fields),
    [normalized, fields]
  );

  // Customer column visibility — see the `cols.unshift` block below for
  // the full rationale. Derived from the FULL `rows` set (not
  // `filteredData`) so the column stays consistent when search/filters
  // narrow down to one tenant; otherwise it would pop in/out as the
  // user types, which feels jumpy.
  const hasCrossTenantRows = useMemo(
    () => rows.some((r) => r.childTag === true),
    [rows]
  );
  const showCustomerColumn = isViewingAllCustomers || hasCrossTenantRows;

  const filteredData = useMemo(
    () =>
      rows.filter(
        (row) =>
          searchPredicate(row) &&
          matchesTagSelection(row, tagFilterSelection) &&
          matchesCategorySelection(row, categoryFilterSelection) &&
          matchesProductSelection(row, productFilterSelection)
      ),
    [
      rows,
      searchPredicate,
      tagFilterSelection,
      categoryFilterSelection,
      productFilterSelection,
    ]
  );

  const stats = useMemo(() => {
    const totalTags = filteredData.length;
    const totalResources = filteredData.reduce(
      (acc, r) => acc + r.resourceCount,
      0
    );
    const categories = new Set(
      filteredData
        .map((r) => r.category?.name)
        .filter((c): c is string => Boolean(c))
    ).size;
    const unused = filteredData.filter((r) => r.resourceCount === 0).length;
    return { totalTags, totalResources, categories, unused };
  }, [filteredData]);

  const openCreate = useCallback(() => {
    navigate("/account/tags-manager/new");
  }, [navigate]);

  const openEdit = useCallback(
    (tag: Tag) => {
      navigate(`/account/tags-manager/${tag.id}`);
    },
    [navigate]
  );

  const handleQuickDelete = useCallback(
    async (tag: Tag) => {
      // Manual flow per TAGS-API-CONTRACT §3.5: backend refuses DELETE
      // while the tag is attached to any resource. We pre-check the
      // count and block the action when N > 0, pointing the user to
      // edit mode (Step 2) so they can detach explicitly.
      const count = await getTagResourceCount(api, mount, tag.id, tenantId);

      if (count > 0) {
        const goEdit = await confirm({
          title: `Can't delete "${tag.name}" yet`,
          description: `This tag is attached to ${count} resource${
            count === 1 ? "" : "s"
          }. Open the tag and detach them before deleting.`,
          confirmLabel: "Open tag",
          cancelLabel: "Close",
          variant: "warning",
        });
        if (goEdit) {
          navigate(`/account/tags-manager/${tag.id}`);
        }
        return;
      }

      const confirmed = await confirm({
        title: `Delete "${tag.name}"?`,
        description: "This action cannot be undone.",
        confirmLabel: "Yes, delete it",
        cancelLabel: "Cancel",
        variant: "warning",
      });
      if (!confirmed) return;

      try {
        await deleteTag(api, mount, tag.id, tenantId);
        await alert({
          title: "Tag deleted",
          description: `"${tag.name}" was removed.`,
          confirmLabel: "OK",
          variant: "success",
        });
      } catch (e) {
        // 409 = a resource was attached between our pre-check and the
        // delete (race). Surface the new count + offer "Open tag".
        if (e instanceof TagDeleteConflictError) {
          const goEdit = await confirm({
            title: `Couldn't delete "${tag.name}"`,
            description: `It is now attached to ${e.resourceCount} resource${
              e.resourceCount === 1 ? "" : "s"
            }. Detach them and try again.`,
            confirmLabel: "Open tag",
            cancelLabel: "Close",
            variant: "warning",
          });
          if (goEdit) {
            navigate(`/account/tags-manager/${tag.id}`);
          }
          return;
        }
        await alert({
          title: "Couldn't delete tag",
          description:
            e instanceof Error
              ? e.message
              : "An unexpected error occurred. Please try again.",
          confirmLabel: "OK",
          variant: "error",
        });
      }
    },
    [api, mount, tenantId, navigate, confirm, alert]
  );

  const columns = useMemo<GenericTableColumn<TagRow>[]>(() => {
    const cols: GenericTableColumn<TagRow>[] = baseColumns.map((col) => {
      if (col.header === "Tag") {
        return {
          ...col,
          renderCell: (row: TagRow) => {
            // `childTag` rows belong to a different tenant in the
            // hierarchy (a child customer in partner/GA multi-tenant
            // view). Per contract §3.1 the flag is informational only —
            // it does NOT block edition: a Global Admin or Partner can
            // write into a child tag (writes follow the same hierarchy
            // at the persistence layer). So every row is an edit link.
            // Ownership is shown exclusively in the dedicated "Customer"
            // column (below) — no inline subtitle here.
            return (
              <button
                type="button"
                onClick={() => openEdit(row)}
                className="flex items-center gap-2 min-w-0 w-full text-left cursor-pointer hover:underline focus:underline active:underline font-medium text-semantic-text-brand"
              >
                <span
                  aria-hidden
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{
                    background:
                      row.color ?? "var(--color-primitive-gray-300)",
                  }}
                />
                <span className="truncate min-w-0">{row.name}</span>
              </button>
            );
          },
        };
      }
      return col;
    });

    // Surface the owning customer at the start of the row — same
    // placement / shape used by MainPort / FwaasPage. The trigger fires
    // in two cases so we don't miss the Global Admin findAll scenario:
    //  1. Partner view (`isViewingAllCustomers`) — the lens convention
    //     shared with the rest of the platform.
    //  2. ANY row in the current set has `childTag === true` — catches
    //     Global Admin browsing the whole system without an explicit
    //     `?tenantId=` lens (per contract §3.0). Without this, GA would
    //     see rows from multiple tenants but no Customer column.
    // Customer-only sessions never satisfy either condition (they only
    // see their own tenant), so the column stays hidden for them.
    // The Tags API doesn't expose `chargebeeCustomerId` on the embedded
    // tenant, so we only render the name.
    if (showCustomerColumn) {
      cols.unshift({
        header: "Customer",
        accessor: "tenant",
        width: "160px",
        wrapMode: "truncate",
        sortAccessor: (row: TagRow) =>
          (row.tenant?.name ?? "").toLowerCase(),
        renderCell: (row: TagRow) =>
          row.tenant?.name ? (
            <span className="font-medium text-semantic-text-primary truncate block">
              {row.tenant.name}
            </span>
          ) : (
            <span className="text-semantic-text-secondary">—</span>
          ),
      });
    }

    // Actions are available in every view. Edition follows the tenant
    // hierarchy server-side (contract §3.0): a Global Admin can edit/delete
    // every tag they see, a Partner can edit/delete own + direct children,
    // so child tags (`childTag: true`) are actionable too. We no longer
    // hide the column in multi-tenant view.
    cols.push({
      header: "Actions",
      renderCell: (row: TagRow) => (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openEdit(row);
            }}
            className="flex items-center justify-center border border-semantic-border-secondary rounded text-semantic-text-secondary hover:bg-semantic-state-active w-8 h-8"
            aria-label={`Edit ${row.name}`}
            title="Edit"
          >
            <IconEdit className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              void handleQuickDelete(row);
            }}
            className="flex items-center justify-center border border-semantic-border-secondary rounded text-semantic-text-secondary hover:bg-semantic-state-active hover:text-semantic-text-error w-8 h-8"
            aria-label={`Delete ${row.name}`}
            title="Delete"
          >
            <IconTrash className="w-4 h-4" />
          </button>
        </div>
      ),
      // Not `fixed: "right"` on purpose — only two icon buttons, the
      // whole table is sized to fit without horizontal scroll, so we
      // don't need the sticky overhead (and avoid the bg-paint quirk
      // sticky cells used to have on row hover).
      width: "100px",
    });

    return cols;
  }, [openEdit, handleQuickDelete, showCustomerColumn]);

  // The page's own shape in skeleton, inside the content area. The overlay this
  // replaces covered the rail and the views bar too (CTX-1122).
  if (showInitialSkeleton) {
    return (
      <>
        <ListPageSkeleton columns={columns.length} />
        {Alert}
      </>
    );
  }

  if (error) {
    return (
      <div className="p-6 space-y-4">
        <ErrorFetchingItems text={error} />
        <div className="flex justify-center">
          <GenericButton
            label="Retry"
            variant="secondary"
            size="md"
            onClick={() => {
              void refresh();
            }}
          />
        </div>
        {Alert}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="p-6">
        <EmptyItems actionLabel="New tag" onAction={openCreate} />
        {Alert}
      </div>
    );
  }

  return (
    <div className="relative p-6 space-y-6">
      <RefreshingIndicator active={isRefreshingList} />
      <GenericCard
        isCollapsible={false}
        variant="primary"
        bodyContent={
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Tags" value={stats.totalTags} hint="Total defined" />
            <Stat
              label="Tagged resources"
              value={stats.totalResources}
              hint="Across all products"
            />
            <Stat
              label="Categories"
              value={stats.categories}
              hint="Distinct categories"
            />
            <Stat
              label="Unused tags"
              value={stats.unused}
              accent={stats.unused > 0 ? "warning" : "success"}
              hint={
                stats.unused > 0
                  ? "Not attached to any resource"
                  : "Every tag is in use"
              }
            />
          </div>
        }
      />

      <GenericCard
        isCollapsible={false}
        className="flex flex-col"
        bodyContent={
          <div className="flex flex-col">
            <div className="mb-2 sm:mb-4 bg-[var(--semantic-bg-primary)] flex-shrink-0">
              {/* One row at every width: on a phone "New tag" stays beside the
                  search instead of alone on a line below it. */}
              <div className="flex items-center gap-3 sm:justify-between my-3 px-1 sm:px-0">
                <div className="flex-1 sm:flex-none w-full max-w-full sm:max-w-[500px] min-w-0">
                  <GenericSearchbar
                    placeholder="Find by attribute or value"
                    suggestedKeys={suggestedKeys}
                    onFiltersChange={setActiveFilters}
                    liveFiltering
                    debounceMs={200}
                  />
                </div>
                <div className="flex justify-end shrink-0 sm:pl-4">
                  <GenericButton
                    label="New tag"
                    variant="brand"
                    size="md"
                    onClick={openCreate}
                  />
                </div>
              </div>
            </div>

            <div className="flex-1 min-h-0 flex flex-col">
              <GenericTable<TagRow>
                columns={columns}
                data={filteredData}
                // The Customer column, when shown, comes first; the card still
                // opens with the tag.
                mobileCards={{
                  titleColumn: "Tag",
                  headlineColumn: "Description",
                  headlineLines: 2,
                  actionColumns: ["Actions"],
                  sortControl: true,
                  itemNoun: "tag",
                }}
                loading={loading && rows.length > 0}
                initialRowsPerPage={10}
                rowsPerPageOptions={[10, 20, 30]}
                width="100%"
                tableClassName="bg-semantic-bg-primary"
                maxTableBodyHeight="100%"
              />
            </div>
          </div>
        }
      />

      {Alert}
    </div>
  );
};

export default TagsManagerPage;
